import { useEffect, type ReactNode } from 'react';
import { useAuth } from './PrivyAuthProvider';
import { useBypassConnectors } from './bypass/BypassContext';
import { navigateToSafeRedirect } from './redirect';

interface LoginPageProps {
  /** App title shown above the login button. Default: "Ops Hub". */
  title?: string;
  /** Subtitle / description below the title. */
  subtitle?: string;
  /** Optional custom logo node (defaults to a simple gradient mark). */
  logo?: ReactNode;
  /**
   * When true (default), watches `authenticated` and navigates to
   * ?redirect=<path> (or "/" if missing) on successful login.
   * Set false if this page is a standalone subsystem that should stay put.
   */
  redirectOnSuccess?: boolean;
  /** Fallback path if ?redirect is missing/unsafe. Default "/". */
  fallbackRedirect?: string;
}

export function LoginPage({
  title = 'Ops Hub',
  subtitle = 'Team members only. Connect your team wallet to continue.',
  logo,
  redirectOnSuccess = true,
  fallbackRedirect = '/',
}: LoginPageProps) {
  const { login, ready, authenticated } = useAuth();
  const bypass = useBypassConnectors();

  useEffect(() => {
    if (!redirectOnSuccess) return;
    if (!ready || !authenticated) return;
    const hasRedirect = new URLSearchParams(window.location.search).has('redirect');
    if (!hasRedirect) return;
    navigateToSafeRedirect(fallbackRedirect);
  }, [ready, authenticated, redirectOnSuccess, fallbackRedirect]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-primary/5">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border bg-card/80 backdrop-blur-sm shadow-sm dark:shadow-none p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            {logo ?? <DefaultLogo />}
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {subtitle}
              </p>
            </div>
          </div>

          {bypass ? (
            <BypassButtons bypass={bypass} />
          ) : (
            <button
              type="button"
              disabled={!ready}
              onClick={login}
              className="mt-8 w-full inline-flex items-center justify-center rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors"
            >
              {ready ? 'Connect Wallet' : 'Loading…'}
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed">
          {bypass ? (
            <>
              Dev bypass mode — Privy SIWE / allowlist skipped.
              <br />
              Both paths connect a real wallet; signing works as in production.
            </>
          ) : (
            <>
              Only whitelisted team wallets are allowed.
              <br />
              Contact an admin if you need access.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function BypassButtons({ bypass }: { bypass: NonNullable<ReturnType<typeof useBypassConnectors>> }) {
  return (
    <div className="mt-8 space-y-3">
      <button
        type="button"
        onClick={bypass.connectInjected}
        className="w-full inline-flex items-center justify-between gap-2 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors"
      >
        <span>Connect browser wallet</span>
        <span className="text-[10px] font-normal opacity-80">EIP-6963</span>
      </button>

      <button
        type="button"
        onClick={bypass.connectWalletConnect}
        disabled={!bypass.privyReady}
        className="w-full inline-flex items-center justify-between gap-2 rounded-lg border bg-background hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2.5 text-sm font-medium text-foreground transition-colors"
      >
        <span>More options</span>
        <span className="text-[10px] font-normal text-muted-foreground">
          {bypass.privyReady ? 'WalletConnect via Privy' : 'loading…'}
        </span>
      </button>
    </div>
  );
}

/**
 * Default brand mark — used when no `logo` prop is passed. Designed to match
 * the small mark in TopBar (gradient square) but at hero size for the login
 * page. SVG-only, no external assets needed.
 */
function DefaultLogo() {
  return (
    <div
      aria-hidden
      className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400/90 to-indigo-500/90 dark:from-sky-500/80 dark:to-indigo-600/80 shadow-sm flex items-center justify-center"
    >
      <svg
        viewBox="0 0 32 32"
        className="w-8 h-8 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="5" width="9" height="9" rx="1.5" />
        <rect x="18" y="5" width="9" height="9" rx="1.5" />
        <rect x="5" y="18" width="9" height="9" rx="1.5" />
        <rect x="18" y="18" width="9" height="9" rx="1.5" />
      </svg>
    </div>
  );
}
